/**
 * CORE_WEB_HOST — core web's PlatformWebHost (@elmes/plugin-sdk/web), mounted at the App root in every
 * build. Thin live delegates to core services (a vi.mock of a service is seen through them); every
 * rejection is normalized to PlatformWebHostRequestError.
 */
import { lazy, Suspense, type ReactElement } from 'react';
import axios from 'axios';
import {
  PlatformWebHostRequestError,
  type PlatformWebHost,
  type PlatformWebHostEntityListProps,
} from '@elmes/plugin-sdk/web';
import { ListLoadingSpinner } from '../modules/desktop/composer/entity/ListLoadingSpinner.js';
import { aiService } from '../services/ai.service.js';
import { entityService } from '../services/entity.service.js';
import { pipelineService } from '../services/pipeline.service.js';

// Lazy: EntityList (react-query, Table, EntityFormModal) must not join the first chunk.
const LazyEntityList = lazy(() =>
  import('../modules/desktop/composer/entity/EntityList.js').then((m) => ({ default: m.EntityList })),
);

// Until the chunk lands: EntityList's own loading view, so the caller's loading state has no blank gap.
function HostEntityList({ definition, onRowClick }: PlatformWebHostEntityListProps): ReactElement {
  return (
    <Suspense fallback={<ListLoadingSpinner />}>
      <LazyEntityList definition={definition} onRowClick={onRowClick} />
    </Suspense>
  );
}

// The axios error (request config with the Authorization header, raw response) stays in core.
function toRequestError(err: unknown): PlatformWebHostRequestError {
  const message = err instanceof Error ? err.message : String(err);
  if (!axios.isAxiosError(err)) return new PlatformWebHostRequestError(message);
  const body: unknown = err.response?.data;
  const code = typeof body === 'object' && body !== null ? (body as { code?: unknown }).code : undefined;
  return new PlatformWebHostRequestError(message, {
    status: err.response?.status,
    code: typeof code === 'string' ? code : undefined,
  });
}

function request<A extends unknown[], R>(call: (...args: A) => Promise<R>): (...args: A) => Promise<R> {
  return async (...args) => {
    try {
      return await call(...args);
    } catch (err) {
      throw toRequestError(err);
    }
  };
}

const pipeline: PlatformWebHost['pipeline'] = {
  list: request((params) => pipelineService.list(params)),
  getRun: request((pipelineId: number, runId: number) => pipelineService.getRun(pipelineId, runId)),
  // Only the contract's options reach core; debug/breakpoint/simulation stay unreachable.
  startRun: request((pipelineId: number, opts?: Parameters<PlatformWebHost['pipeline']['startRun']>[1]) =>
    pipelineService.startRun(
      pipelineId,
      opts === undefined ? undefined : { triggerType: opts.triggerType, triggerData: opts.triggerData },
    ),
  ),
};

const host: PlatformWebHost = {
  executeTool: request((tool: string, params?: Record<string, unknown>) => aiService.executeTool(tool, params)),
  getEntityDefinition: request((slug: string) => entityService.getDefinition(slug)),
  pipeline: Object.freeze(pipeline),
  components: Object.freeze({ EntityList: HostEntityList }),
};

export const CORE_WEB_HOST: PlatformWebHost = Object.freeze(host);
